// Notification inbox — App Flow §2, deep links per Content §4.4.
import {
  Button,
  EmptyState,
  LoadingState,
  NotificationItem,
  PageHeader,
  useToast,
} from '@roots/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api.js';
import { relativeTime } from '../lib/format.js';

export function NotificationsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications({ limit: 50 }),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.markRead(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAll = useMutation({
    mutationFn: () => api.markAllRead(),
    onSuccess: () => {
      push('All caught up!');
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Notifications"
        action={
          (data?.unread_count ?? 0) > 0 ? (
            <Button variant="ghost" onClick={() => markAll.mutate()}>
              Mark all read
            </Button>
          ) : undefined
        }
      />

      {isLoading && <LoadingState onRetry={() => void refetch()} />}

      {!isLoading && (data?.notifications.length ?? 0) === 0 && (
        <EmptyState message="No notifications yet. They’ll show up here when your family is active." />
      )}

      <div className="flex flex-col gap-2">
        {data?.notifications.map((n) => (
          <NotificationItem
            key={n.id}
            type={n.type}
            title={n.title}
            body={n.body}
            timestamp={relativeTime(n.created_at)}
            familyName={n.family_name}
            unread={!n.is_read}
            onClick={() => {
              if (!n.is_read) markRead.mutate(n.id);
              const link = n.payload_json.deep_link;
              if (link) void navigate({ to: link });
            }}
          />
        ))}
      </div>
    </div>
  );
}
